<!--
  The account picker body: a grouped, searchable combobox over one namespace of targets
  (accounts and contacts alike). Groups render as greyed, collapsible headers with their
  children indented beneath; a search keeps matching ancestors visible. ARIA combobox 1.2:
  the input owns role="combobox", the popup is a listbox of options, and the highlighted one
  is tracked with aria-activedescendant so focus stays in the text field.
-->
<script setup lang="ts">
import { AccountIcon } from '@taler/ui'
import { computed, inject, nextTick, onUnmounted, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { CONTACT_CREATOR_KEY } from '../internal/contact-creator.ts'
import { FAVOURITE_ACCOUNT_KEY } from '../internal/favourite.ts'
import {
  buildPickerRows,
  isNavigable,
  isShortcutKey,
  type PickerAction,
  type PickerRow,
} from '../internal/picker-rows.ts'
import ContactQuickCreateDialog from '@/features/entries/components/ContactQuickCreateDialog.vue'
import { frecencyWhen, type Frecency, type Target } from '../internal/targets.ts'

const props = withDefaults(
  defineProps<{
    id: string
    label: string
    modelValue: string | null | undefined
    /** The ledger `modelValue` belongs to, when a caller tracks a cross-ledger choice
     * separately (AGENTS.md's picker) - a fixture account id can coincide across ledgers,
     * so plain id lookup alone cannot tell which same-id target is the real selection. */
    ledgerId?: string | undefined
    targets: readonly Target[]
    frecency?: ReadonlyMap<string, Frecency>
    hint?: ((target: Target) => string | undefined) | undefined
    /** A reason makes the target visible but not selectable. */
    unavailable?: ((target: Target) => string | undefined) | undefined
    grouped?: boolean
    compact?: boolean
    disabled?: boolean
    accountsLabel?: string | undefined
    /** This field accepts contacts, so it may offer creating one inline. */
    allowCreateContact?: boolean
    placeholder?: string | undefined
  }>(),
  { frecency: () => new Map(), grouped: true, compact: false, disabled: false, allowCreateContact: false },
)

const emit = defineEmits<{
  'update:modelValue': [string | undefined]
  accept: []
  /** The full chosen target, so a cross-ledger caller can read its `ledgerId` alongside its id. */
  select: [Target]
}>()

const { t } = useI18n()
const contactCreator = inject(CONTACT_CREATOR_KEY, undefined)
const favouriteAccountId = inject(FAVOURITE_ACCOUNT_KEY, undefined)
const creatable = computed(() => props.allowCreateContact && contactCreator?.enabled() === true)
const isCreating = ref(false)
const createError = ref<string | undefined>(undefined)
const dialogName = ref<string | undefined>(undefined)

function frecencyHint(frecency: Frecency): string {
  const when = frecencyWhen(frecency)
  const whenText =
    when === 'today'
      ? t('entries.frecencyToday')
      : when === 'yesterday'
        ? t('entries.frecencyYesterday')
        : when === 'daysAgo'
          ? t('entries.frecencyDaysAgo', { count: frecency.lastUsedDaysAgo })
          : undefined
  return whenText === undefined
    ? t('entries.frecencyCount', { count: frecency.count })
    : t('entries.frecencyCountWhen', { count: frecency.count, when: whenText })
}

const inputRef = useTemplateRef<HTMLInputElement>('input')
const text = ref('')
const filter = ref('')
const isOpen = ref(false)
const highlightIndex = ref(0)
const collapsed = ref<ReadonlySet<string>>(new Set())
const popoverStyle = ref<Record<string, string>>({})

// Teleported: a grid's horizontal-scroll wrapper clips any in-place absolute child.
function updatePopoverPosition(): void {
  const rect = inputRef.value?.getBoundingClientRect()
  if (rect === undefined) return
  const fitsBelow = window.innerHeight - rect.bottom > 320 || rect.top < 320
  const width = Math.max(rect.width, 320)
  popoverStyle.value = {
    left: `${Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))}px`,
    width: `${width}px`,
    ...(fitsBelow ? { top: `${rect.bottom + 2}px` } : { bottom: `${window.innerHeight - rect.top + 2}px` }),
  }
}

const selected = computed(() =>
  props.targets.find((target) => target.id === props.modelValue && target.ledgerId === props.ledgerId),
)

function displayText(target: Target | undefined): string {
  if (target === undefined) return ''
  if (props.compact) return target.leaf
  return [...(target.ancestors ?? []).map((ancestor) => ancestor.name), target.leaf].join(' / ')
}
function selectionText(): string {
  return displayText(selected.value)
}
function syncTextToSelection(): void {
  text.value = selectionText()
}
watch(() => [props.modelValue, props.ledgerId, props.targets], syncTextToSelection, { immediate: true })

const rows = computed(() =>
  buildPickerRows(props.targets, {
    query: filter.value,
    collapsed: collapsed.value,
    grouped: props.grouped,
    frecency: props.frecency,
    favouriteId: favouriteAccountId?.value,
    unavailable: props.unavailable,
    creatable: creatable.value,
    labels: {
      favourite: t('entries.pickerFavourite'),
      recent: t('entries.pickerRecent'),
      accounts: props.accountsLabel ?? t('entries.pickerAccounts'),
      contacts: t('entries.pickerContacts'),
    },
  }),
)
const navigable = computed(() => rows.value.filter(isNavigable))
const highlighted = computed(() => navigable.value[highlightIndex.value])

function optionDomId(key: string): string {
  return `${props.id}-option-${key.replaceAll(':', '-')}`
}

function onReposition(): void {
  if (isOpen.value) updatePopoverPosition()
}
window.addEventListener('scroll', onReposition, true)
window.addEventListener('resize', onReposition)
onUnmounted(() => {
  window.removeEventListener('scroll', onReposition, true)
  window.removeEventListener('resize', onReposition)
})

function openList(): void {
  updatePopoverPosition()
  isOpen.value = true
  const current = navigable.value.findIndex(
    (row) =>
      row.kind === 'option' && row.target.id === props.modelValue && row.target.ledgerId === props.ledgerId,
  )
  highlightIndex.value = Math.max(current, 0)
}

function closeList(): void {
  isOpen.value = false
  filter.value = ''
  createError.value = undefined
}

function highlightParts(name: string): { before: string; match: string; after: string } {
  const needle = filter.value.trim()
  const index = needle === '' ? -1 : name.toLowerCase().indexOf(needle.toLowerCase())
  if (index === -1) return { before: name, match: '', after: '' }
  return {
    before: name.slice(0, index),
    match: name.slice(index, index + needle.length),
    after: name.slice(index + needle.length),
  }
}

/** A recent or favourite row shows no group header for context, so it needs its own breadcrumb - as a
 * muted hint beside the account's own name, never in place of it. */
function shortcutAncestorPath(row: PickerRow): string | undefined {
  if (row.kind !== 'option' || !isShortcutKey(row.key)) return undefined
  const ancestors = row.target.ancestors
  if (ancestors === undefined || ancestors.length === 0) return undefined
  return ancestors.map((ancestor) => ancestor.name).join(' / ')
}

function select(target: Target): void {
  emit('update:modelValue', target.id)
  emit('select', target)
  text.value = displayText(target)
  closeList()
}

function acceptHighlighted(): boolean {
  const row = highlighted.value
  if (row === undefined) return false
  if (row.kind === 'option') select(row.target)
  else void runAction(row.action)
  return true
}

function actionLabel(action: PickerAction): string {
  if (action.type === 'create') return t('entries.pickerCreateContact', { name: action.name })
  if (action.type === 'existing') return t('entries.pickerUseExisting', { name: action.target.leaf })
  return t('entries.pickerNewContact')
}

async function createContact(input: { name: string; notes?: string }): Promise<boolean> {
  if (contactCreator === undefined || isCreating.value) return false
  const wanted = input.name.trim().toLowerCase()
  const existing = props.targets.find(
    (target) => target.source === 'contact' && target.leaf.trim().toLowerCase() === wanted,
  )
  if (existing !== undefined) {
    select(existing)
    return true
  }
  isCreating.value = true
  createError.value = undefined
  const result = await contactCreator.create(input)
  isCreating.value = false
  if (!result.ok) {
    createError.value = result.detail ?? t('entries.pickerCreateFailed')
    return false
  }
  emit('update:modelValue', result.accountId)
  text.value = result.name
  closeList()
  return true
}

async function runAction(action: PickerAction): Promise<void> {
  if (action.type === 'existing') return select(action.target)
  if (action.type === 'create') {
    await createContact({ name: action.name })
    return
  }
  dialogName.value = filter.value.trim()
  closeList()
}

async function onDialogCreate(input: { name: string; notes?: string }): Promise<string | undefined> {
  const created = await createContact(input)
  if (!created) return createError.value
  dialogName.value = undefined
  return undefined
}

function toggleGroup(id: string): void {
  const next = new Set(collapsed.value)
  if (!next.delete(id)) next.add(id)
  collapsed.value = next
}

function collapseOrAscend(): void {
  const row = highlighted.value
  if (row?.kind !== 'option') return
  if (row.expandable && !row.collapsed) return toggleGroup(row.target.id)
  const parentId = row.target.ancestors?.at(-1)?.id
  const parentIndex = navigable.value.findIndex(
    (candidate) =>
      candidate.kind === 'option' && candidate.key.startsWith('option:') && candidate.target.id === parentId,
  )
  if (parentIndex >= 0) highlightIndex.value = parentIndex
}

function onInput(): void {
  filter.value = text.value
  if (!isOpen.value) openList()
  highlightIndex.value = 0
  if (text.value.trim() === '' && props.modelValue !== null && props.modelValue !== undefined)
    emit('update:modelValue', undefined)
}

function onFocus(): void {
  openList()
  void nextTick(() => inputRef.value?.select())
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    if (!isOpen.value) return openList()
    highlightIndex.value = Math.min(highlightIndex.value + 1, navigable.value.length - 1)
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    highlightIndex.value = Math.max(highlightIndex.value - 1, 0)
  } else if (event.key === 'Home' && isOpen.value) {
    event.preventDefault()
    highlightIndex.value = 0
  } else if (event.key === 'End' && isOpen.value) {
    event.preventDefault()
    highlightIndex.value = Math.max(navigable.value.length - 1, 0)
  } else if (event.key === 'ArrowRight' && isOpen.value && filter.value === '') {
    const row = highlighted.value
    if (row?.kind === 'option' && row.expandable && row.collapsed) {
      event.preventDefault()
      toggleGroup(row.target.id)
    }
  } else if (event.key === 'ArrowLeft' && isOpen.value && filter.value === '') {
    event.preventDefault()
    collapseOrAscend()
  } else if (event.key === 'Enter') {
    if (isOpen.value) {
      event.preventDefault()
      acceptHighlighted()
    }
    emit('accept')
  } else if (event.key === 'Tab') {
    if (isOpen.value && filter.value !== '') acceptHighlighted()
    closeList()
  } else if (event.key === 'Escape') {
    if (isOpen.value) event.preventDefault()
    syncTextToSelection()
    closeList()
  }
}

function onBlur(): void {
  // A blur that leaves no real selection reverts: the model only ever holds a real target id.
  if (selected.value === undefined) text.value = ''
  else syncTextToSelection()
  closeList()
}

function isHighlighted(row: PickerRow): boolean {
  return (row.kind === 'option' || row.kind === 'action') && highlighted.value?.key === row.key
}

watch(highlighted, (row) => {
  if (row === undefined) return
  void nextTick(() => document.getElementById(optionDomId(row.key))?.scrollIntoView?.({ block: 'nearest' }))
})

defineExpose({ focus: () => inputRef.value?.focus() })

const popoverClass =
  'pointer-events-auto fixed z-[60] max-h-80 overflow-y-auto rounded-[var(--radius-overlay)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] py-[var(--space-tight)] shadow-[var(--shadow-overlay)]'

const showAdornments = computed(() => !props.compact && selected.value !== undefined && !isOpen.value)
</script>

<template>
  <div class="relative">
    <span
      v-if="showAdornments && selected?.icon !== undefined"
      aria-hidden="true"
      class="pointer-events-none absolute left-[6px] top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-[5px] [&_svg]:h-3 [&_svg]:w-3"
      :style="{
        background: `color-mix(in srgb, ${selected.color} 18%, var(--color-surface))`,
        color: selected.color,
      }"
    >
      <AccountIcon :icon="selected.icon" label="" />
    </span>
    <input
      :id="id"
      ref="input"
      v-model="text"
      type="text"
      role="combobox"
      autocomplete="off"
      :aria-label="label"
      aria-autocomplete="list"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      :aria-controls="`${id}-listbox`"
      :aria-activedescendant="isOpen && highlighted ? optionDomId(highlighted.key) : undefined"
      :disabled="disabled"
      :placeholder="placeholder ?? t('entries.pickerPlaceholder')"
      class="min-h-[var(--density-control)] w-full truncate rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[length:var(--text-step-3)] text-[color:var(--color-text)] placeholder:text-[color:var(--color-text-muted)]"
      :class="[
        showAdornments && selected?.icon !== undefined ? 'pl-8' : undefined,
        showAdornments && selected?.currency !== undefined ? 'pr-14' : undefined,
      ]"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
      @keydown="onKeydown"
    />
    <span
      v-if="showAdornments && selected?.currency !== undefined"
      aria-hidden="true"
      class="pointer-events-none absolute right-[6px] top-1/2 -translate-y-1/2 rounded-[var(--radius-control)] bg-[color:var(--color-surface-sunken)] px-[6px] py-[1px] text-[length:var(--text-step-1)] font-medium text-[color:var(--color-text-muted)]"
      data-currency-chip
    >
      {{ selected.currency }}
    </span>
    <Teleport to="body">
      <ul
        v-if="isOpen && rows.length > 0"
        :id="`${id}-listbox`"
        role="listbox"
        :aria-label="label"
        :class="popoverClass"
        :style="popoverStyle"
        @mousedown.prevent
      >
        <template v-for="row in rows" :key="row.key">
          <li
            v-if="row.kind === 'section'"
            role="presentation"
            class="px-[var(--space-cell)] pb-[var(--space-tight)] pt-[var(--space-inline)] text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]"
          >
            {{ row.label }}
          </li>
          <li
            v-else-if="row.kind === 'group'"
            role="option"
            aria-disabled="true"
            :aria-selected="false"
            data-group
            class="flex cursor-pointer items-center gap-[var(--space-inline)] px-[var(--space-cell)] py-[var(--space-inline)] text-[color:var(--color-text-muted)]"
            :style="{ paddingLeft: `calc(var(--space-cell) + ${row.depth * 14}px)` }"
            @mousedown.prevent="toggleGroup(row.id)"
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 16 16"
              aria-hidden="true"
              class="shrink-0 transition-transform"
              :class="row.collapsed ? '-rotate-90' : undefined"
            >
              <path
                d="M4 6.5 8 10.5 12 6.5"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span
              v-if="!compact"
              aria-hidden="true"
              class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] opacity-60 [&_svg]:h-3 [&_svg]:w-3"
              :style="{
                background: `color-mix(in srgb, ${row.color} 18%, var(--color-surface))`,
                color: row.color,
              }"
            >
              <AccountIcon :icon="row.icon" label="" />
            </span>
            <span class="min-w-0 truncate">{{ row.name }}</span>
          </li>
          <li
            v-else-if="row.kind === 'action'"
            :id="optionDomId(row.key)"
            role="option"
            :aria-selected="isHighlighted(row)"
            :aria-busy="isCreating"
            class="flex cursor-pointer items-center gap-[var(--space-inline)] px-[var(--space-cell)] py-[var(--space-inline)] text-[color:var(--color-accent)]"
            :class="[
              isHighlighted(row) ? 'bg-[color:var(--color-surface-raised)]' : undefined,
              isCreating ? 'opacity-60' : undefined,
            ]"
            data-contact-action
            @mousedown.prevent="runAction(row.action)"
            @mouseenter="highlightIndex = navigable.findIndex((candidate) => candidate.key === row.key)"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" class="shrink-0">
              <path d="M8 3v10M3 8h10" fill="none" stroke="currentColor" stroke-width="1.7" />
            </svg>
            <span class="min-w-0 truncate">{{
              isCreating ? t('entries.pickerCreating') : actionLabel(row.action)
            }}</span>
          </li>
          <li
            v-else
            :id="optionDomId(row.key)"
            role="option"
            :aria-selected="isHighlighted(row)"
            :aria-disabled="row.disabledReason !== undefined ? 'true' : undefined"
            :aria-expanded="row.expandable ? !row.collapsed : undefined"
            class="flex items-center gap-[var(--space-inline)] px-[var(--space-cell)] py-[var(--space-inline)]"
            :class="[
              row.disabledReason !== undefined ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
              isHighlighted(row) ? 'bg-[color:var(--color-surface-raised)]' : undefined,
            ]"
            :style="{ paddingLeft: `calc(var(--space-cell) + ${row.depth * 14}px)` }"
            @mousedown.prevent="row.disabledReason === undefined && select(row.target)"
            @mouseenter="
              row.disabledReason === undefined &&
              (highlightIndex = navigable.findIndex((candidate) => candidate.key === row.key))
            "
          >
            <span
              v-if="row.expandable"
              aria-hidden="true"
              data-expand
              class="-ml-1 inline-flex h-5 w-5 shrink-0 items-center justify-center text-[color:var(--color-text-muted)]"
              @mousedown.prevent.stop="toggleGroup(row.target.id)"
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 16 16"
                class="transition-transform"
                :class="row.collapsed ? '-rotate-90' : undefined"
              >
                <path
                  d="M4 6.5 8 10.5 12 6.5"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
            <span
              v-if="!compact && row.target.icon !== undefined && row.target.color !== undefined"
              aria-hidden="true"
              class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] [&_svg]:h-3 [&_svg]:w-3"
              :style="{
                background: `color-mix(in srgb, ${row.target.color} 18%, var(--color-surface))`,
                color: row.target.color,
              }"
            >
              <AccountIcon :icon="row.target.icon" label="" />
            </span>
            <span class="min-w-0 flex-1 truncate text-[color:var(--color-text)]" :title="row.target.path">
              <span>{{ highlightParts(row.target.leaf).before }}</span
              ><span class="text-[color:var(--color-accent)]">{{
                highlightParts(row.target.leaf).match
              }}</span
              ><span>{{ highlightParts(row.target.leaf).after }}</span
              ><span
                v-if="shortcutAncestorPath(row) !== undefined"
                class="text-[color:var(--color-text-muted)]"
              >
                &nbsp;· {{ shortcutAncestorPath(row) }}</span
              >
            </span>
            <span
              v-if="row.disabledReason !== undefined"
              class="shrink-0 text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
            >
              {{ row.disabledReason }}
            </span>
            <span
              v-else-if="hint?.(row.target) !== undefined"
              class="shrink-0 whitespace-nowrap text-[length:var(--text-step-1)] font-medium text-[color:var(--color-text-muted)]"
            >
              {{ hint?.(row.target) }}
            </span>
            <span
              v-if="row.key.startsWith('recent:') && frecency.get(row.target.id) !== undefined"
              class="shrink-0 whitespace-nowrap text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
            >
              {{ frecencyHint(frecency.get(row.target.id)!) }}
            </span>
            <span
              v-if="!compact && row.target.currency !== undefined"
              class="shrink-0 rounded-[var(--radius-control)] bg-[color:var(--color-surface-sunken)] px-[6px] py-[1px] text-[length:var(--text-step-1)] font-medium text-[color:var(--color-text-muted)]"
              data-currency-chip
            >
              {{ row.target.currency }}
            </span>
          </li>
        </template>
        <li
          v-if="createError !== undefined"
          role="presentation"
          class="px-[var(--space-cell)] py-[var(--space-inline)] text-[length:var(--text-step-1)] text-[color:var(--color-danger)]"
        >
          {{ createError }}
        </li>
      </ul>
      <p
        v-else-if="isOpen"
        :class="popoverClass"
        :style="popoverStyle"
        class="px-[var(--space-cell)] py-[var(--space-block)] text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
      >
        {{ t('entries.pickerNoMatches') }}
      </p>
    </Teleport>
    <ContactQuickCreateDialog
      v-if="dialogName !== undefined"
      :initial-name="dialogName"
      :create="onDialogCreate"
      @close="dialogName = undefined"
    />
  </div>
</template>
